/**
 * Right panel — the IDENTICAL shell in all three modes:
 * two tabs [Cars] [#NNN DriverName] over the panel body.
 *
 * Tab semantics are mode-owned (passed in by the mode component):
 *   browse/detail  tab click = GLOBAL mode change (Cars ↔ browse,
 *                  Driver ↔ detail)
 *   theater        tab click switches the rail LOCALLY — no mode change
 *
 * The body is a flex column and the CHILD owns scrolling: CarsTab
 * scrolls its list under a pinned toolbar/search, DriverTab scrolls
 * its sections under the pinned media + driver header (mobile design
 * pins the video while telemetry/events scroll).
 */

import React from 'react';
import { Car } from 'lucide-react';

import { cn } from '@/lib/utils';
import { EventLiveDevice } from '../../hooks/useEventLiveDevices';
import { displayCarNumber } from './carNumber';

export type PanelTab = 'cars' | 'driver';

export const RightPanel: React.FC<{
  active: PanelTab;
  selected: EventLiveDevice | null;
  onTabChange: (tab: PanelTab) => void;
  /** Browse/detail on phones: the bottom Watch/Map/Cars bar owns
   *  switching, so the panel's own tab row only renders on lg
   *  (the mobile viewer mockup has no top tab row). Theater keeps
   *  the row on every viewport — its tabs switch locally. */
  hideTabsOnMobile?: boolean;
  children: React.ReactNode;
  className?: string;
}> = ({ active, selected, onTabChange, hideTabsOnMobile = false, children, className }) => (
  <aside className={cn('flex min-h-0 flex-col bg-bg', className)}>
    <div
      className={cn(
        'shrink-0 border-b border-border-subtle bg-bg',
        hideTabsOnMobile ? 'hidden lg:flex' : 'flex',
      )}
    >
      <button
        type="button"
        onClick={() => onTabChange('cars')}
        className={cn(
          'flex flex-1 items-center justify-center gap-1.5 border-b-2 px-2 py-2.5 text-[10px] font-bold uppercase tracking-wider transition-colors',
          active === 'cars'
            ? 'border-accent bg-bg-surface text-accent'
            : 'border-transparent text-fg-muted hover:text-fg',
        )}
      >
        <Car className="size-3.5" />
        Cars
      </button>
      <button
        type="button"
        disabled={!selected}
        onClick={() => selected && onTabChange('driver')}
        title={selected ? 'Open driver detail' : 'Pick a car first'}
        className={cn(
          'flex flex-1 items-center justify-center gap-1.5 border-b-2 px-2 py-2.5 text-[10px] font-bold uppercase tracking-wider transition-colors',
          active === 'driver'
            ? 'border-accent bg-bg-surface text-accent'
            : 'border-transparent text-fg-muted',
          selected ? 'hover:text-fg' : 'cursor-default opacity-50',
        )}
      >
        {selected ? (
          <>
            <span className="font-mono text-[11px]">{displayCarNumber(selected)}</span>
            <span className="max-w-[120px] truncate normal-case tracking-normal">
              {selected.driver_name || selected.device_id}
            </span>
          </>
        ) : (
          'Driver'
        )}
      </button>
    </div>
    <div className="flex min-h-0 flex-1 flex-col">{children}</div>
  </aside>
);

export default RightPanel;
